import type { AssistantHistoryItem, Timeout } from '@assembly-web/services';
import {
  IconButton,
  OpenTextEditor,
  TabGroupItem,
  TabsGroup,
  TextStyle,
  Tooltip,
} from '@assembly-web/ui';
import {
  InformationCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import dayjs, { duration } from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import ScrollToBottom from 'react-scroll-to-bottom';

import { config } from '../../../../../config';
import { useMultiDrawerStore } from '../../../../../stores/useMultiDrawerStore';
import { useGetDoraChatHistoryQuery } from '../../../hooks/dora/useChatHistoryQuery';
import { useSuggestedChatQuestions } from '../../../hooks/dora/useSuggestedChatQuestions';
import { useUserDetails } from '../../../hooks/useUserDetails';
import { trackDoraAction } from '../../../services/analytics';
import { convertMarkDownResponseToHTML } from '../../../services/dora';
import { setFullScreenDrawerId } from '../../../services/drawers';
import { isAdminMember } from '../../../services/member';
import {
  type createDoraDrawerStore,
  type DoraReportingCategory,
  getDoraChatStore,
  getPersistKeyForDoraChatDrawer,
} from '../../../stores/doraChatStore';
import { SuggestedQuestionsModule } from '../AskDoraDrawer/SuggestedQuestionsModule';
import { BottomActionSheet } from '../shared/dora/BottomActionSheet';
import { ChatHistoryError } from '../shared/dora/ChatHistoryError';
import { ChatHistoryLoading } from '../shared/dora/ChatHistoryLoading';
import { timeouts } from '../shared/dora/constants';
import { DoraMessageBubble } from '../shared/dora/DoraMessageBubble';
import { DoraStackedMessages } from '../shared/dora/DoraStackedMessages';
import type { DoraChatDrawer as DoraChatDrawerProps } from '../types';
import { BetaTag } from './BetaTag';
import { DoraChatReportAnswer } from './DoraChatReportAnswer';
import { DoraChatReportPrompt } from './DoraChatReportPrompt';
import { IntroChatMessage } from './IntroChatMessage';
import { QuestionAnswerBlock } from './QuestionAnswerBlock';
import { ReportingInsightsChatSwitcher } from './ReportingInsightsChatSwitcher';

dayjs.extend(duration);

const messages = defineMessages({
  newDoraChatDrawerTitle: {
    defaultMessage: 'Ask DoraAI',
    id: '71+qWd',
  },
  drawerTitle: {
    defaultMessage: 'Ask DoraAI: {prompt}',
    id: '3McJoM',
  },
  editorLabel: {
    defaultMessage: 'Ask something',
    id: 'Yh9fcS',
  },
  reportingInsightsTabLabel: {
    defaultMessage: 'Reporting & Insights',
    id: 'WAbSLD',
  },
  tabsLabel: {
    defaultMessage: 'You are asking about:',
    id: 'Ud7p8d',
  },
  tryPromptsLabel: {
    defaultMessage: 'Try one of these prompts:',
    id: 'vGEQ8v',
  },
  editorHelperText: {
    defaultMessage: 'What would you like to know?',
    id: 'KXSupW',
  },
  editorHelpDescription: {
    defaultMessage:
      'Like humans, AI can make mistakes. For critical business decisions, please download and verify analyses.',
    id: '/A/IkX',
  },
  restartConversationPlaceholder: {
    defaultMessage:
      'This conversation’s data needs to be refreshed. <button>Start new conversation</button>',
    id: 'NJ+F8H',
  },
  restartConversationChatBubble: {
    defaultMessage:
      'This conversation’s data needs to be refreshed, so you cannot ask any more questions in this thread. <button>Start a new conversation</button> to keep getting insights from your data.',
    id: 'xntjPg',
  },
});

export function ReportingInsightsChatView(
  props: DoraChatDrawerProps & {
    doraChatStore: ReturnType<typeof createDoraDrawerStore>;
    getDoraResponse: (prompt: string, threadId?: string) => void;
    reportingCategory: DoraReportingCategory;
    shouldAnimateIntroMessage: boolean;
  }
) {
  const {
    doraChatStore,
    getDoraResponse,
    id,
    isFullScreen,
    reportingCategory,
    shouldAnimateIntroMessage,
  } = props;

  const { formatMessage } = useIntl();

  const suggestedQuestions = useSuggestedChatQuestions();

  const useDoraChatStore = doraChatStore;
  const draftInputValue =
    useMultiDrawerStore((store) => store.findDrawer(id))?.inputDraftValue ?? '';

  const updateDraftInputValue = useMultiDrawerStore(
    (store) => store.updateDraftInputValue
  );

  const showResponseTimeout = useRef<Timeout>();

  const isStreaming = useDoraChatStore(
    (store) => store.reportingInsights?.recentBlock?.isStreaming ?? false
  );

  const isLoading = useDoraChatStore(
    (store) => store.reportingInsights?.recentBlock?.isLoading ?? false
  );

  const isLoadingSeen = useDoraChatStore(
    (store) => store.reportingInsights?.recentBlock?.isLoadingSeen ?? false
  );

  const isAnswerSeen = useDoraChatStore(
    (store) => store.reportingInsights?.recentBlock?.isAnswerSeen ?? false
  );

  const [isResponseBubbleVisible, setIsResponseBubbleVisible] = useState(
    isLoadingSeen || isAnswerSeen
  );

  const currentBlock = useDoraChatStore(
    (state) => state.reportingInsights?.recentBlock
  );

  const curPrompt = useDoraChatStore(
    (state) => state.reportingInsights?.recentBlock?.prompt
  );

  const addPrompt = useDoraChatStore((state) => state.addPrompt);

  const threadId = useDoraChatStore(
    (store) => store.reportingInsights?.threads?.[0].id ?? ''
  );

  const {
    data: chatHistoryData,
    isError: isChatHistoryError,
    isInitialLoading: isChatHistoryLoading,
    refetch: refetchChatHistory,
  } = useGetDoraChatHistoryQuery(threadId, {
    enabled: Boolean(threadId),
  });

  const lastMessageCreatedAt = dayjs(
    (
      chatHistoryData?.findLast(
        (elem) => elem.role === 'assistant'
      ) as AssistantHistoryItem | null
    )?.createdAt ?? new Date()
  );

  const isLastMessagePastCutOffTime =
    dayjs().diff(lastMessageCreatedAt, 'minutes') >= 60;

  const findAndUpdateDrawerField = useMultiDrawerStore(
    (store) => store.findAndUpdateDrawerField
  );

  const deleteDrawer = useMultiDrawerStore((store) => store.deleteDrawer);
  const closeDrawer = useMultiDrawerStore((store) => store.closeDrawer);
  const createDrawer = useMultiDrawerStore((store) => store.createDrawer);
  const findDrawer = useMultiDrawerStore((store) => store.findDrawer);

  useEffect(() => {
    return () => clearTimeout(showResponseTimeout.current);
  }, []);

  const [{ data: userDetails }] = useUserDetails();

  if (!userDetails) {
    return null;
  }

  function changeCategory(category: DoraReportingCategory) {
    closeDrawer(props.id);

    const drawerId = createDrawer({
      type: 'doraChat',
      title: formatMessage(messages.newDoraChatDrawerTitle),
      data: {},
    });

    const drawer = findDrawer(drawerId);

    if (drawer) {
      const persistKey = getPersistKeyForDoraChatDrawer(drawer);

      const { doraChatStore: newStore } = getDoraChatStore(persistKey);
      newStore.getState().setReportingCategory(category);
      setFullScreenDrawerId(drawerId);
    }
  }

  const isAdmin = isAdminMember(userDetails.member);

  const updateResponseChatBubble = (prompt: string) => {
    if ((chatHistoryData ?? []).length === 0) {
      findAndUpdateDrawerField(id, (draft) => {
        draft.title = formatMessage(messages.drawerTitle, { prompt });
      });
    }

    clearTimeout(showResponseTimeout.current);
    setIsResponseBubbleVisible(false);

    showResponseTimeout.current = setTimeout(
      () => setIsResponseBubbleVisible(true),
      timeouts.showInput
    );
  };

  const handlePromptSubmit = () => {
    getDoraResponse(draftInputValue, threadId);
    updateResponseChatBubble(draftInputValue);

    updateDraftInputValue(id, '');
  };

  const getSuggestedQuestions = () => {
    const filteredByCategory = suggestedQuestions.filter(
      (suggestion) => suggestion.category === reportingCategory
    );

    const filteredByRole = filteredByCategory.filter((suggestion) => {
      const { member } = userDetails;
      if (isAdminMember(member)) {
        return suggestion.role === 'admin';
      }

      if (member.profile.isManager) {
        return suggestion.role === 'manager';
      }

      return false;
    });

    return filteredByRole.map((suggestion) => suggestion.question);
  };

  let scrollContent = null;

  if (isChatHistoryLoading) {
    scrollContent = <ChatHistoryLoading />;
  } else if (isChatHistoryError) {
    scrollContent = <ChatHistoryError onTryAgain={refetchChatHistory} />;
  } else {
    const chatBubbles = [];
    const chatMessages = chatHistoryData ?? [];

    for (let i = 0; i < chatMessages.length; i++) {
      const message = chatMessages[i];

      if (message.role === 'user') {
        // To address the edge case that the user manually refreshes the page before Dora's full answer is returned,
        // only show user messages that have a corresponding Dora answer from chat history.
        if (chatMessages[i + 1]?.role === 'assistant') {
          chatBubbles.push(
            <DoraChatReportPrompt
              memberDetails={userDetails.member}
              prompt={message.content}
              shouldAnimate={false}
            />
          );
        }
      } else {
        const responses: AssistantHistoryItem[] = [message];

        while (i + 1 < chatMessages.length) {
          const nextMessage = chatMessages[i + 1];

          if (nextMessage.role === 'assistant') {
            responses.push(nextMessage);
            i++;
          } else {
            break;
          }
        }

        let markdownContent: string[] = [];

        const htmlMessages = responses.map((response) => {
          let fullContent = response.content;

          if (response.imageUrl) {
            if (fullContent) {
              fullContent += '\n\n';
            }

            fullContent += `![](${config.domains.doraApi}${response.imageUrl})`;
          }

          markdownContent.push(fullContent);

          return convertMarkDownResponseToHTML(fullContent, {
            showCursor: false,
          }) as string;
        });

        chatBubbles.push(
          <DoraChatReportAnswer
            showFeedbackControl={!isStreaming}
            promptId={message.promptId}
            rawResponse={markdownContent.join('\n\n')}
            htmlResponses={htmlMessages}
            key={`${props.id}-Assistant-${i}`}
            isAnswerSeen={false}
            isLoading={false}
            shouldAnimate={false}
          />
        );
      }
    }

    scrollContent = (
      <>
        <div className="flex h-full flex-col justify-end overflow-auto">
          <ScrollToBottom
            className="overflow-auto"
            followButtonClassName="hidden"
            initialScrollBehavior="auto"
          >
            <section className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-2 gap-y-4 overflow-auto p-6">
              <IntroChatMessage
                isAdmin={isAdmin}
                reportingCategory={reportingCategory}
                shouldAnimate={shouldAnimateIntroMessage}
                userFirstName={userDetails.member.profile.firstName}
              />
              {chatBubbles}
              {Boolean(isLastMessagePastCutOffTime) && (
                <DoraStackedMessages shouldAnimateAvatar={false}>
                  <DoraMessageBubble>
                    <TextStyle subdued>
                      {formatMessage(messages.restartConversationPlaceholder, {
                        button: (elem: ReactNode) => (
                          <button
                            className="underline"
                            onClick={() => {
                              trackDoraAction('clickedTimeoutStartNew');
                              changeCategory(reportingCategory);
                            }}
                          >
                            {elem}
                          </button>
                        ),
                      })}
                    </TextStyle>
                  </DoraMessageBubble>
                </DoraStackedMessages>
              )}
              {Boolean(curPrompt) && (
                <QuestionAnswerBlock
                  doraChatStore={doraChatStore}
                  getDoraResponse={getDoraResponse}
                  isFullScreen={isFullScreen}
                  isResponseBubbleVisible={isResponseBubbleVisible}
                  member={userDetails.member}
                />
              )}
            </section>
          </ScrollToBottom>
        </div>
        <BottomActionSheet shouldAnimateOnMount={false}>
          <div className="mx-6 my-2">
            <AnimatePresence>
              {Boolean(chatHistoryData && chatHistoryData.length > 1) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: '100%', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <TextStyle
                    variant="sm-regular"
                    subdued
                    className="inline-flex items-center gap-1 text-center"
                  >
                    {formatMessage(messages.editorHelperText)}
                    <Tooltip
                      tooltipText={formatMessage(
                        messages.editorHelpDescription
                      )}
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                    </Tooltip>
                  </TextStyle>
                </motion.div>
              )}
            </AnimatePresence>

            {isLastMessagePastCutOffTime ? (
              <div className="inline-flex min-h-[90px] w-full flex-col rounded-lg border border-gray-7 bg-gray-2 p-2">
                <TextStyle subdued>
                  {formatMessage(messages.restartConversationPlaceholder, {
                    button: (elem: ReactNode) => (
                      <button
                        className="underline"
                        onClick={() => {
                          trackDoraAction('clickedTimeoutStartNew');
                          changeCategory(reportingCategory);
                        }}
                      >
                        {elem}
                      </button>
                    ),
                  })}
                </TextStyle>
                <section className="flex flex-row-reverse p-2">
                  <IconButton disabled>
                    <PaperAirplaneIcon className="h-6 w-6 text-gray-7" />
                  </IconButton>
                </section>
              </div>
            ) : (
              <>
                <OpenTextEditor
                  aria-label={formatMessage(messages.editorLabel)}
                  autoFocusInput
                  className="max-h-[174px]"
                  isSubmitDisabled={draftInputValue.trim() === '' || isLoading}
                  maxLength={500}
                  onChange={(e) => updateDraftInputValue(id, e.target.value)}
                  onSubmit={handlePromptSubmit}
                  placeholder={formatMessage(messages.editorLabel)}
                  rows={1}
                  shouldSubmitOnEnter
                  disabled={currentBlock?.isLoading}
                  value={draftInputValue}
                />
                <div className="mt-2">
                  <SuggestedQuestionsModule
                    disabled={currentBlock?.isLoading}
                    prompt={formatMessage(messages.tryPromptsLabel)}
                    onSuggestedQuestionClick={(selectedQuestion) => {
                      const selectedSuggestion = suggestedQuestions.find(
                        (suggestion) => suggestion.question === selectedQuestion
                      );

                      if (selectedSuggestion) {
                        const { text } = selectedSuggestion;

                        addPrompt(text);
                        getDoraResponse(text, threadId);

                        updateResponseChatBubble(text);
                      }
                    }}
                    suggestedQuestions={getSuggestedQuestions()}
                  />
                </div>
              </>
            )}
          </div>
        </BottomActionSheet>
      </>
    );
  }

  return (
    <div className="flex h-full flex-col antialiased">
      <div className="border-b border-b-gray-5 bg-gray-1 px-4 py-2">
        <div className="flex h-6 w-full flex-row items-center">
          <TextStyle variant="xs-regular" subdued className="mr-1">
            {formatMessage(messages.tabsLabel)}
          </TextStyle>
          <TabsGroup type="single" value="reportingInsights" className="mb-0">
            <TabGroupItem
              className="mx-0 mr-[1px] flex h-6 flex-row items-center gap-x-2 rounded-l rounded-r-none px-2 py-1"
              value="reportingInsights"
            >
              <TextStyle variant="sm-medium">
                {formatMessage(messages.reportingInsightsTabLabel)}
              </TextStyle>
              <BetaTag className="h-5 w-10 rounded-l-lg rounded-r-lg bg-gray-1 text-gray-9" />
            </TabGroupItem>
          </TabsGroup>

          <ReportingInsightsChatSwitcher
            currentCategory={reportingCategory}
            onCategoryChange={(category: DoraReportingCategory) => {
              if (category === reportingCategory) {
                return;
              }

              trackDoraAction(
                category === 'recognition'
                  ? 'switchToRecognitionReportingCategory'
                  : category === 'flows'
                    ? 'switchToFlowEngagementReportingCategory'
                    : 'switchToMilestonesReportingCategory'
              );

              changeCategory(category);

              const isEmptyConversation =
                !(isLoading || isStreaming) && !chatHistoryData;

              if (isEmptyConversation) {
                deleteDrawer(id);
              }
            }}
          />
        </div>
      </div>
      {scrollContent}
    </div>
  );
}
